// Shared rules for investor branded handles (deckscore.ai/<handle>).

export const RESERVED_HANDLES: ReadonlySet<string> = new Set([
  // Existing app routes / concepts
  "upload", "results", "apply", "apply-success", "dashboard", "login", "logout",
  "admin", "tasks", "investor", "for-investors", "analytics", "contact",
  "sentry-test", "privacy-policy", "terms-and-conditions", "shipping-policy",
  "refund-policy",
  // Future / infra
  "api", "auth", "assets", "static", "r", "i", "og", "terms", "privacy",
  "security", "support", "jobs", "about", "help", "docs", "blog", "press",
  "pricing", "features", "settings", "profile", "account", "billing",
  "signup", "sign-up", "sign-in", "signin", "register", "onboarding",
  // Files that must never be shadowed
  "robots.txt", "sitemap.xml", "favicon.ico", "manifest.json", "sw.js",
  "index", "index.html",
]);

const HANDLE_RE = /^[a-z0-9][a-z0-9-]{1,28}[a-z0-9]$/;

export type HandleCheck =
  | { ok: true; handle: string }
  | { ok: false; reason: "empty" | "format" | "length" | "reserved" };

export function normalizeHandle(raw: string): string {
  return (raw || "").trim().toLowerCase();
}

export function validateHandle(raw: string): HandleCheck {
  const h = normalizeHandle(raw);
  if (!h) return { ok: false, reason: "empty" };
  if (h.length < 3 || h.length > 30) return { ok: false, reason: "length" };
  if (!HANDLE_RE.test(h)) return { ok: false, reason: "format" };
  if (RESERVED_HANDLES.has(h)) return { ok: false, reason: "reserved" };
  return { ok: true, handle: h };
}

/** Slugify a firm name into a valid candidate handle. */
export function slugifyHandle(name: string): string {
  let s = (name || "")
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  if (s.length < 3) s = (s + "-inv").replace(/^-+|-+$/g, "");
  if (s.length > 30) s = s.slice(0, 30).replace(/-+$/g, "");
  return s || "investor";
}

export function handleReasonMessage(reason: Exclude<HandleCheck, { ok: true }>["reason"]): string {
  switch (reason) {
    case "empty": return "Handle is required.";
    case "length": return "Handle must be 3-30 characters.";
    case "format": return "Use lowercase letters, numbers, and hyphens.";
    case "reserved": return "That handle is reserved. Try another.";
  }
}

/** Shared referral copy so /<handle> page and /og/<handle> image never drift. */
export function referralHeadline(firm: string): string {
  return `${firm} backs founders who sweat the details.`;
}
export const REFERRAL_DEFAULT_SUBLINE =
  "Sharpen your deck to investor-grade with an honest readiness read - then it comes straight to me.";

